import { Product } from "src/api/client-api/products/useGetOneProduct";
import {
    ProductFormProductCompany,
    ProductFormProductConsumption,
    ProductFormProductIngredient,
    ProductFormProductPackaging,
    ProductFormProductTransformations,
    ProductFormProductTransport,
    ProductFormType,
    SuppliersName,
} from "./ProductForm/ProductForm.types";
import {
    ProductConsumptionWrite,
    ProductIngredientWrite,
    ProductPackagingWrite,
    ProductTransformationWrite,
    ProductTransportWrite,
    ProductWrite,
} from "src/api/client-api/products/useUpsertProduct";
import { ProductReference } from "src/api/sure-api/productReference/useGetOneProductReference";

export type ProductFormWriteOptions = {
    isProductDistributionEnabled?: boolean;
    isProductConsumptionEnabled?: boolean;
};

export class ProductService {
    static filterProductJsonAttributesForWrite = (
        jsonAttributes: Record<string, unknown> | undefined,
    ): Record<string, unknown> | undefined => {
        if (!jsonAttributes) {
            return undefined;
        }

        const filtered = Object.fromEntries(
            Object.entries(jsonAttributes).filter(([, value]) => value !== "" && value !== null && value !== undefined),
        );

        return Object.keys(filtered).length > 0 ? filtered : undefined;
    };

    static getProductToCreate = (options: {
        existingProduct: Product | undefined;
        productReference: ProductReference | undefined;
        clone?: boolean;
    }): ProductFormType => {
        const { existingProduct, productReference, clone = false } = options;
        if (existingProduct === undefined) {
            return {
                id: undefined,
                title: null,
                clientId: null,
                productReference: productReference ?? null,
                netWeight: null,
                brand: null,
                ssq_id: null,
                productLine: null,
                distributionReferenceId: null,
                tags: {},
                productPackagings: [],
                productIngredients: [],
                productTransformations: [],
                productTransports: [],
                productConsumptions: [],
                photos: [],
                pendingProductPhotos: [],
                ownedBy: null,
                shares: [],
                isIntermediate: false,
                organic: false,
            };
        }

        const existingPIng = existingProduct.productIngredients?.map((pIng) => {
            const formattedPIng: ProductFormProductIngredient = {
                "@id": !clone ? pIng["@id"] : undefined,
                id: !clone ? pIng.id : undefined,
                quantity: pIng.quantity ?? null,
                usedQuantity: pIng.usedQuantity ?? null,
                quantityOverridden: true,
                origin: pIng.origin ?? null,
                resolvedTransports: pIng.resolvedTransports ?? [],
            };

            // Handle regular ingredient
            if (pIng.ingredient) {
                const suppliersName: SuppliersName[] =
                    pIng.ingredient.suppliers?.map((ingSup) => {
                        return { id: ingSup.supplier.id, name: ingSup.supplier.name };
                    }) ?? [];
                formattedPIng.ingredient = {
                    "@id": pIng.ingredient["@id"],
                    id: pIng.ingredient.id,
                    name: pIng.ingredient.name,
                    origin: pIng.ingredient.origin ?? null,
                    ingredientReferenceId: pIng.ingredient.ingredientReferenceId,
                    referenceSource: pIng.ingredient.referenceSource,
                    suppliersName: suppliersName,
                    canView: pIng.ingredient.canView,
                };
            }

            // Handle intermediate product as ingredient
            if (pIng.intermediateProduct) {
                formattedPIng.intermediateProduct = {
                    "@id": pIng.intermediateProduct["@id"],
                    id: pIng.intermediateProduct.id,
                    title: pIng.intermediateProduct.title,
                };
            }

            return formattedPIng;
        });

        const existingPTrans = existingProduct.productTransformations?.map((pTrans) => {
            const formattedPtrans: ProductFormProductTransformations = {
                "@id": !clone ? pTrans["@id"] : undefined,
                id: !clone ? pTrans.id : undefined,
                transformationReferenceId: pTrans.transformationReferenceId,
                transformationCode: pTrans.transformationCode,
                transformationNameFr: pTrans.transformationNameFr,
                massGrams: pTrans.massGrams ?? null,
                organization: pTrans.organization ?? null,
                position: pTrans.position ?? 0,
            };
            return formattedPtrans;
        });

        const existingPTransport = existingProduct.productTransports?.map((pT) => {
            const formatted: ProductFormProductTransport = {
                "@id": !clone ? pT["@id"] : undefined,
                id: !clone ? pT.id : undefined,
                productIngredient: pT.productIngredient ?? null,
                productPackaging: pT.productPackaging ?? null,
                stage: pT.stage,
                transportCircuit: {
                    "@id": pT.transportCircuit["@id"],
                    id: pT.transportCircuit.id,
                    name: pT.transportCircuit.name,
                    linkMode: pT.transportCircuit.linkMode,
                    sourceReferenceCircuitId: pT.transportCircuit.sourceReferenceCircuitId,
                },
            };
            return formatted;
        });

        const existingPCons = existingProduct.productConsumptions?.map((pCons) => {
            const formatted: ProductFormProductConsumption = {
                "@id": !clone ? pCons["@id"] : undefined,
                id: !clone ? pCons.id : undefined,
                consumptionReferenceId: pCons.consumptionReferenceId,
                consumptionCode: pCons.consumptionCode,
                consumptionNameFr: pCons.consumptionNameFr,
            };
            return formatted;
        });

        const existingPPack = existingProduct.productPackagings?.map((pPack) => {
            const formattedPPack: ProductFormProductPackaging = {
                "@id": pPack["@id"],
                id: pPack.id,
                quantity: pPack.quantity ?? null,
                usedWeight: pPack.usedWeight ?? null,
            };
            if (pPack.packaging) {
                formattedPPack.packaging = {
                    "@id": pPack.packaging["@id"],
                    "@type": pPack.packaging["@type"],
                    id: pPack.packaging.id,
                    name: pPack.packaging.name,
                    externalId: pPack.packaging.externalId,
                    weight: pPack.packaging.weight,
                    canView: pPack.packaging.canView,
                };
            }

            if (
                pPack.packagingReferenceId !== undefined &&
                pPack.packagingReferenceKind !== undefined &&
                pPack.packagingReferenceId !== null &&
                pPack.packagingReferenceKind !== null
            ) {
                const packagingReferenceId = pPack.packagingReferenceId;
                const packagingReferenceKind = pPack.packagingReferenceKind;
                formattedPPack.packagingReference = {
                    compositeId: `${packagingReferenceKind}-${packagingReferenceId}`,
                    id: packagingReferenceId,
                    kind: packagingReferenceKind,
                    name: pPack.packagingReferenceName ?? "",
                };
            }

            return formattedPPack;
        });

        // Use tags directly - API returns them in the correct format
        const tags: Record<string, string[]> = existingProduct.tags || {};

        const prodCompanies =
            existingProduct.shares?.map((pc) => ({
                "@id": pc["@id"],
                organization: pc.organization,
            })) ?? [];

        return {
            id: !clone ? existingProduct.id : undefined,
            title: existingProduct.title,
            clientId: existingProduct.clientId ?? null,
            productReference: productReference ?? null,
            netWeight: existingProduct.netWeight ?? null,
            brand: existingProduct.brand ?? null,
            productLine: existingProduct.productLine ?? null,
            distributionReferenceId: existingProduct.distributionReferenceId ?? null,
            distribution: existingProduct.distribution ?? null,
            tags: tags,
            jsonAttributes: existingProduct.jsonAttributes ?? {},
            productIngredients: existingPIng ?? [],
            productConsumptions: existingPCons ?? [],
            productTransformations: existingPTrans ?? [],
            productTransports: existingPTransport ?? [],
            productPackagings: existingPPack ?? [],
            photos: existingProduct.photos ?? [],
            pendingProductPhotos: [],
            ownedBy: existingProduct.ownedBy ?? null,
            shares: prodCompanies,
            ssq_id: existingProduct.ssqId ?? null,
            isIntermediate: existingProduct.isIntermediate ?? false,
            organic: existingProduct.organic ?? false,
        };
    };

    private static productIngredientFormToWrite = (
        productIngredient: ProductFormProductIngredient[],
    ): ProductIngredientWrite[] => {
        return productIngredient.map((pi) => {
            if (pi["@id"] === undefined) {
                // New product ingredient
                const baseWrite = {
                    quantity: pi.quantity,
                    usedQuantity: pi.usedQuantity,
                    isUsedQuantityEqualToQuantity: false,
                };

                // Either ingredient or intermediateProduct must be set
                if (pi.ingredient) {
                    return {
                        ...baseWrite,
                        ingredient: pi.ingredient["@id"],
                    };
                } else if (pi.intermediateProduct) {
                    return {
                        ...baseWrite,
                        intermediateProduct: pi.intermediateProduct["@id"],
                    };
                } else if (pi.ingredientReference) {
                    return {
                        ...baseWrite,
                        ingredientReferenceId: pi.ingredientReference.id,
                        referenceSource: pi.ingredientReference.source,
                    };
                } else {
                    throw new Error(
                        "ProductIngredient must have either ingredient, intermediateProduct, or ingredientReference",
                    );
                }
            }

            // Existing product ingredient (update) — include entity link when present so ingredient swaps persist
            const baseUpdate = {
                "@id": pi["@id"],
                quantity: pi.quantity,
                usedQuantity: pi.usedQuantity,
                isUsedQuantityEqualToQuantity: false,
            };

            if (pi.ingredient) {
                return {
                    ...baseUpdate,
                    ingredient: pi.ingredient["@id"],
                };
            }

            if (pi.intermediateProduct) {
                return {
                    ...baseUpdate,
                    intermediateProduct: pi.intermediateProduct["@id"],
                };
            }

            if (pi.ingredientReference) {
                return {
                    ...baseUpdate,
                    ingredientReferenceId: pi.ingredientReference.id,
                    referenceSource: pi.ingredientReference.source,
                };
            }

            return baseUpdate;
        });
    };

    private static productPackagingsFormToWrite = (
        productPackagings: ProductFormProductPackaging[],
    ): ProductPackagingWrite[] => {
        return productPackagings.flatMap((pPack) => {
            if (pPack["@id"] === undefined) {
                if (pPack.packaging) {
                    return {
                        packaging: pPack.packaging["@id"],
                        quantity: pPack.quantity,
                        usedWeight: null,
                    };
                }

                if (pPack.packagingReference) {
                    return {
                        packagingReferenceId: pPack.packagingReference.id,
                        packagingReferenceKind: pPack.packagingReference.kind,
                        quantity: pPack.quantity,
                        usedWeight: pPack.usedWeight ?? null,
                    };
                }

                throw new Error("ProductPackaging must have either packaging or packagingReference");
            }

            return {
                "@id": pPack["@id"],
                quantity: pPack.quantity,
                usedWeight: pPack.packaging ? null : (pPack.usedWeight ?? null),
            };
        });
    };

    private static productConsumptionFormToWrite = (
        productConsumptions: ProductFormProductConsumption[],
    ): ProductConsumptionWrite[] => {
        return productConsumptions.map((pCons) => {
            if (pCons["@id"] !== undefined) {
                return {
                    "@id": pCons["@id"],
                    consumptionReferenceId: pCons.consumptionReferenceId,
                };
            }

            return {
                consumptionReferenceId: pCons.consumptionReferenceId,
            };
        });
    };

    private static productTransportFormToWrite = (
        productTransports: ProductFormProductTransport[],
    ): ProductTransportWrite[] => {
        return productTransports
            .filter((pT) => pT.draftProductIngredientKey == null)
            .map((pT) => {
                const circuitIri =
                    pT.transportCircuit["@id"] ?? (`/api/transport-circuits/${pT.transportCircuit.id}` as IRI);

                if (pT["@id"] !== undefined) {
                    return {
                        "@id": pT["@id"],
                        productIngredient: pT.productIngredient?.["@id"] ?? null,
                        productPackaging: pT.productPackaging?.["@id"] ?? null,
                        stage: pT.stage,
                        transportCircuit: circuitIri,
                    };
                }

                return {
                    productIngredient: pT.productIngredient?.["@id"] ?? null,
                    productPackaging: pT.productPackaging?.["@id"] ?? null,
                    stage: pT.stage,
                    transportCircuit: circuitIri,
                };
            });
    };

    private static productTransformationFormToWrite = (
        productTransformations: ProductFormProductTransformations[],
    ): ProductTransformationWrite[] => {
        return productTransformations.map((pT) => {
            if (pT["@id"] !== undefined) {
                return {
                    "@id": pT["@id"],
                    transformationReferenceId: pT.transformationReferenceId,
                    massGrams: pT.massGrams ?? null,
                    organization: pT.organization?.["@id"] ?? null,
                    position: pT.position ?? 0,
                };
            }

            return {
                transformationReferenceId: pT.transformationReferenceId,
                massGrams: pT.massGrams ?? null,
                organization: pT.organization?.["@id"] ?? null,
                position: pT.position ?? 0,
            };
        });
    };

    private static makeProductCompanies = (productCompanies: ProductFormProductCompany[]): any[] => {
        return productCompanies.map((prodComp) => {
            const prodCompId = prodComp["@id"];
            if (prodCompId) {
                return prodCompId;
            }

            return {
                organization: prodComp.organization["@id"],
            };
        });
    };

    static productFormToProductWrite = (
        productForm: ProductFormType,
        ownedBy: IRI | undefined,
        options: ProductFormWriteOptions = {},
    ): ProductWrite => {
        const isProductDistributionEnabled = options.isProductDistributionEnabled ?? true;
        const isProductConsumptionEnabled = options.isProductConsumptionEnabled ?? true;
        const isExistingProduct = productForm.id !== undefined;
        const shouldSerializeDistribution = isProductDistributionEnabled || isExistingProduct;
        const shouldSerializeConsumption = isProductConsumptionEnabled || isExistingProduct;

        const newPI = ProductService.productIngredientFormToWrite(productForm.productIngredients);
        const newPack = ProductService.productPackagingsFormToWrite(productForm.productPackagings);
        const newProdCompanies = ProductService.makeProductCompanies(productForm.shares ?? []);
        const newTransformations = ProductService.productTransformationFormToWrite(
            productForm.productTransformations ?? [],
        );

        const productWrite: ProductWrite = {
            id: productForm.id,
            ownedBy: productForm.ownedBy?.["@id"] ?? ownedBy,

            title: productForm.title,
            clientId: productForm.clientId,
            productReferenceId: productForm.productReference?.id ?? null,
            netWeight: productForm.netWeight,
            brand: productForm.brand?.["@id"] ?? null,
            productLine: productForm.productLine?.["@id"] ?? null,
            jsonAttributes: ProductService.filterProductJsonAttributesForWrite(productForm.jsonAttributes),
            tags: productForm.tags ?? undefined,

            productIngredients: newPI,
            productPackagings: newPack,
            shares: newProdCompanies,
            productConsumptions:
                shouldSerializeConsumption ?
                    ProductService.productConsumptionFormToWrite(productForm.productConsumptions ?? [])
                :   [],
            productTransformations: newTransformations,
            productTransports: ProductService.productTransportFormToWrite(productForm.productTransports ?? []),
            distributionReferenceId: shouldSerializeDistribution ? productForm.distributionReferenceId : null,
            distribution: shouldSerializeDistribution ? (productForm.distribution?.["@id"] ?? null) : null,

            ssqId: productForm.ssq_id ?? null,
            isIntermediate: productForm.isIntermediate ?? false,
            organic: productForm.organic ?? false,
        };

        return productWrite;
    };

    static roundTo = (value: number, precision: number): number => {
        const newValue = Math.round(value * Math.pow(10, precision)) / Math.pow(10, precision);
        return newValue;
    };
}
